import React, { createContext, useContext, useState, useEffect } from 'react';
import { favoriteService } from '../services/favoriteService';
import { useAuth } from './AuthContext'; // Import useAuth

const FavoritesContext = createContext({
  favoritedSubjects: new Set(),
  favoriteLoading: {},
  isLoading: true,
  isFavorited: () => false,
  addFavorite: () => { },
  removeFavorite: () => { },
  setFavoriteLoading: () => { },
  refreshFavorites: () => { },
});

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    // console.error('useFavorites must be used within a FavoritesProvider');
    // Return a fallback object to prevent crashes and ensure properties are defined
    return {
      favoritedSubjects: new Set(),
      favoriteLoading: {},
      isLoading: true,
      isFavorited: () => false,
      addFavorite: () => { },
      removeFavorite: () => { },
      setFavoriteLoading: () => { },
      refreshFavorites: () => { },
    };
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favoritedSubjects, setFavoritedSubjects] = useState(new Set());
  const [favoriteLoading, setFavoriteLoading] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth(); // Get both isAuthenticated and isLoading from AuthContext

  // Fetch user's favorites on mount or when authentication status changes
  useEffect(() => {
    const fetchFavorites = async () => {
      // Wait for auth to finish loading before attempting to fetch
      if (authLoading) {
        return;
      }

      if (!isAuthenticated) {
        setIsLoading(false);
        setFavoritedSubjects(new Set()); // Clear favorites if not authenticated
        return;
      }
      try {
        setIsLoading(true);
        const response = await favoriteService.getFavorites(1, 100);
        const { data } = response.data;
        if (data && Array.isArray(data)) {
          const subjectIds = new Set(data.map(fav => String(fav.subject_id || fav.subject?.id)));
          setFavoritedSubjects(subjectIds);
          // console.log('FavoritesContext: Fetched and set favorited subjects:', Array.from(subjectIds));
          // console.log('FavoritesContext: Raw favorites data:', data);
        }
      } catch (error) {
        // console.error('Error fetching favorites:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFavorites();
  }, [isAuthenticated, authLoading]); // Add authLoading to dependency array

  const addFavorite = (subjectId) => {
    setFavoritedSubjects(prev => new Set([...prev, String(subjectId)]));
  };

  const removeFavorite = (subjectId) => {
    setFavoritedSubjects(prev => {
      const newSet = new Set(prev);
      newSet.delete(String(subjectId));
      // console.log('FavoritesContext: Removed favorite, new favoritedSubjects:', Array.from(newSet));
      return newSet;
    });
  };

  const isFavorited = (subjectId) => {
    // const subjectIdStr = String(subjectId);
    const result = favoritedSubjects.has(String(subjectId));
    // console.log(`FavoritesContext: isFavorited(${subjectId}) called, checking for "${subjectIdStr}", result: ${result}`);
    // console.log(`FavoritesContext: favoritedSubjects contains:`, Array.from(favoritedSubjects));
    return result;
  };

  const refreshFavorites = async () => {
    try {
      setIsLoading(true);
      const response = await favoriteService.getFavorites(1, 100);
      const { data } = response.data;
      if (data && Array.isArray(data)) {
        const subjectIds = new Set(data.map(fav => String(fav.subject_id || fav.subject?.id)));
        setFavoritedSubjects(subjectIds);
        // console.log('FavoritesContext: Refreshed favorited subjects:', Array.from(subjectIds));
      }
    } catch (error) {
      // console.error('Error refreshing favorites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    favoritedSubjects,
    favoriteLoading,
    isLoading,
    isFavorited,
    addFavorite,
    removeFavorite,
    setFavoriteLoading,
    refreshFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

